import { portsTakenByDrops, sideOf, tracePath } from './connections.ts';
import { isPanel, isPortList, portEnabled } from './item-types.ts';
import type { ClientIndex } from './connections.ts';
import type { Connection } from './schemas/connection.ts';
import type { Item } from './schemas/item.ts';

// The connection state of one numbered port (ROADMAP A6), computed from the
// trace so the rack drawing, the ports screen, and the item page colour a
// port the same way. Four states, one colour key:
//   complete   – both ends of the port's connection reach an active device
//                (a run patched all the way through, no dangling hop).
//   incomplete – the connection ends at a passive hop: a panel with a free
//                side, or a run that lands in a room with nothing patched on.
//   landed     – a run is punched down on the port with no link yet ("keystone
//                in" on a panel, a drop landed on a switch port).
//   none       – nothing on the port.
// A disabled port keeps its state but is drawn dimmed, the way the faceplate
// already dims a switched-off port; that is a display concern, not a state.

export type PortConnectionState = 'complete' | 'incomplete' | 'landed' | 'none';

/** The states in the order the shared colour key lists them. */
export const PORT_STATE_ORDER: readonly PortConnectionState[] = [
  'complete',
  'incomplete',
  'landed',
  'none',
];

/** What each state is called wherever the key is drawn. */
export const PORT_STATE_LABELS: Readonly<Record<PortConnectionState, string>> = {
  complete: 'Complete',
  incomplete: 'Incomplete',
  landed: 'Run landed',
  none: 'Free',
};

/**
 * The resolved colour of each state, kept in step with the `--port-*` tokens
 * in `tokens.css`. The faceplate and the on-screen key read the CSS tokens
 * directly; a server-side drawing (the report SVG, which has no stylesheet)
 * and the cabling colour key (`shared/wiring-colors.ts`) read these so print
 * and screen show a port in the same semantic colour: complete green,
 * incomplete amber, a landed run blue, a free port grey.
 */
export const PORT_STATE_HEX: Readonly<Record<PortConnectionState, string>> = {
  complete: '#1b7f3b',
  incomplete: '#b7791a',
  landed: '#2457d6',
  none: '#8a94a6',
};

/** The dark-theme half of {@link PORT_STATE_HEX}, matching the same tokens. */
export const PORT_STATE_HEX_DARK: Readonly<Record<PortConnectionState, string>> = {
  complete: '#6fd48f',
  incomplete: '#f4b95a',
  landed: '#8ab4ff',
  none: '#6b7686',
};

/** The connections with an end on exactly this device port (either A or B side). */
function linksOnPort(
  connections: readonly Connection[],
  itemId: string,
  port: number,
): Connection[] {
  return connections.filter((connection) =>
    [connection.a, connection.b].some((end) => end.itemId === itemId && end.port === port),
  );
}

/** Whether a cable drop is punched down on this port (from either end of the run). */
function dropLandsOnPort(index: ClientIndex, itemId: string, port: number): boolean {
  return portsTakenByDrops(index.drops, itemId).some((landing) => landing.port === port);
}

/** A trace hop that terminates the path at a real, active device rather than a passive hop. */
function endsAtActiveDevice(index: ClientIndex, hop: { itemId: string } | undefined): boolean {
  if (hop === undefined || hop.itemId === '') {
    return false;
  }
  const item = index.items.get(hop.itemId);
  // A patch panel is a passive hop: a trace that stops at one has not reached
  // a device on that side, so the connection is incomplete there.
  return item !== undefined && !isPanel(item.type);
}

/**
 * The state of `port` on `item`: the four A6 states from the trace. A port
 * with a link is complete only when both ends of its trace reach an active
 * device; a link that dead-ends at a panel or a bare run is incomplete.
 */
export function portConnectionState(
  index: ClientIndex,
  connections: readonly Connection[],
  item: Pick<Item, 'id'>,
  port: number,
): PortConnectionState {
  const links = linksOnPort(connections, item.id, port);
  if (links.length === 0) {
    return dropLandsOnPort(index, item.id, port) ? 'landed' : 'none';
  }
  const hops = tracePath(index, connections, item.id, port);
  const first = hops[0];
  const last = hops.at(-1);
  return endsAtActiveDevice(index, first) && endsAtActiveDevice(index, last)
    ? 'complete'
    : 'incomplete';
}

/**
 * The link ids that make up a port's connection chain (ROADMAP A5), so a
 * hovered or focused port can light every segment of the run it belongs to.
 * Empty for a free or landed-only port, since an unconnected port never
 * highlights.
 */
export function chainLinkIds(
  index: ClientIndex,
  connections: readonly Connection[],
  itemId: string,
  port: number,
): Set<string> {
  const start = index.items.get(itemId);
  if (start === undefined) {
    return new Set<string>();
  }
  const ids = new Set<string>();
  // Walk the same links the trace visits, on whichever sides the trace uses.
  const sides = isPanel(start.type) ? (['front', 'rear'] as const) : ([null] as const);
  for (const side of sides) {
    collectChain(connections, itemId, port, side, ids);
  }
  return ids;
}

/** Follow the connection out of (`itemId`, `port`) on `side`, gathering link ids as it goes. */
function collectChain(
  connections: readonly Connection[],
  itemId: string,
  port: number,
  side: 'front' | 'rear' | null,
  ids: Set<string>,
): void {
  const link = connections.find(
    (connection) =>
      !ids.has(connection.id) &&
      (side === null || sideOf(connection) === side) &&
      [connection.a, connection.b].some((end) => end.itemId === itemId && end.port === port),
  );
  if (link === undefined) {
    return;
  }
  ids.add(link.id);
  const far = link.a.itemId === itemId ? link.b : link.a;
  if (far.port === null) {
    return;
  }
  // Through a panel the chain leaves by the opposite side; a device ends it.
  const arrivedOn = sideOf(link);
  const leaveBy: 'front' | 'rear' = arrivedOn === 'front' ? 'rear' : 'front';
  collectChain(connections, far.itemId, far.port, side === null ? null : leaveBy, ids);
}

/** Whether a port should be drawn dimmed: recorded and switched off (C2). */
export function portDimmed(item: Pick<Item, 'details'>, port: number): boolean {
  const ports = item.details['ports'];
  if (!isPortList(ports)) {
    return false;
  }
  const row = ports.find((entry) => entry.number === port);
  return row !== undefined && !portEnabled(row);
}
